import React, { CSSProperties } from "react";
import { Col, Row, Tooltip, Typography } from "@douyinfe/semi-ui";
import { AiCoachMetrics } from "../../../api/aiCoach";
import { METRIC_SPECS, MetricSpec } from "../constants";
import { radius, sp, toneBg, toneColor, Tone } from "../styles";

const { Text } = Typography;

interface Props {
  metrics: AiCoachMetrics;
  /** 点指标卡查看明细；timeAllocationRatio 不会触发 */
  onBreakdown?: (metricKey: string, label: string) => void;
}

function evalTone(spec: MetricSpec, value: number | undefined | null): Tone {
  if (value == null) return "neutral";
  const v = Number(value);
  if (spec.alertOnPositive) {
    if (v === 0) return "good";
    if (v >= spec.warn) return "bad";
    return "warn";
  }
  if (spec.direction === "higher") {
    if (v >= spec.good) return "good";
    if (v >= spec.warn) return "warn";
    return "bad";
  }
  // lower is better
  if (v <= spec.good) return "good";
  if (v <= spec.warn) return "warn";
  return "bad";
}

function formatValue(spec: MetricSpec, value: unknown): string {
  const v = Number(value as number);
  if (Number.isNaN(v)) return "-";
  return spec.unit === "pct" ? `${(v * 100).toFixed(0)}%` : String(v);
}

const cardStyle = (tone: Tone): CSSProperties => ({
  height: "100%",
  borderRadius: radius.md,
  borderLeft: `3px solid ${toneColor[tone]}`,
  background: toneBg[tone],
  transition: "transform .15s ease",
});

const valueStyle = (tone: Tone): CSSProperties => ({
  fontSize: 26,
  fontWeight: 600,
  lineHeight: 1.2,
  color: tone === "neutral" ? "var(--semi-color-text)" : toneColor[tone],
});

const MetricCard: React.FC<{
  spec: MetricSpec;
  value: unknown;
  onBreakdown?: (metricKey: string, label: string) => void;
}> = ({ spec, value, onBreakdown }) => {
  const tone = evalTone(spec, typeof value === "number" ? value : Number(value));
  const display = formatValue(spec, value);
  // timeAllocationRatio 是比值指标，无客户集合，不可点
  const clickable = !!onBreakdown && spec.key !== "timeAllocationRatio";
  return (
    <Col span={4} xl={4} lg={6} md={8} sm={8} xs={8}>
      <Tooltip content={clickable ? `${spec.hint} · 点击查看客户明细` : spec.hint} position="top">
        <div
          style={{
            ...cardStyle(tone),
            padding: `${sp.md} ${sp.lg}`,
            cursor: clickable ? "pointer" : "help",
          }}
          onClick={clickable ? () => onBreakdown!(spec.key, spec.label) : undefined}
        >
          <div style={{ display: "block", padding: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text type="tertiary" size="small">{spec.label}</Text>
              {clickable && (
                <Text type="tertiary" size="small" style={{ color: "var(--semi-color-primary)" }}>
                  详情 →
                </Text>
              )}
            </div>
            <div style={{ marginTop: sp.xs }}>
              <span style={valueStyle(tone)}>{display}</span>
            </div>
          </div>
        </div>
      </Tooltip>
    </Col>
  );
};

const MetricsGrid: React.FC<Props> = ({ metrics, onBreakdown }) => (
  <Row gutter={[sp.md, sp.md]} style={{ marginBottom: sp.lg }}>
    {METRIC_SPECS.map((spec) => (
      <MetricCard
        key={spec.key}
        spec={spec}
        value={(metrics as unknown as Record<string, number>)[spec.key]}
        onBreakdown={onBreakdown}
      />
    ))}
  </Row>
);

export default MetricsGrid;
